import React from 'react';
import moment, { Moment } from 'moment';
import { CSSZSickLeaveCertificate } from '../../Interfaces/CSSZ.interface';
import Text from '../../../../Components/Base/Text/Text';
import styles from './CSSZSickLeaveCertificateConfirmationOfDuration.module.scss';
import CSSZSickLeaveCertificateConfirmationOfDurationForm, {
	CSSZSickLeaveCertificateConfirmationOfDurationFormValues,
	CSSZSickLeaveCertificateConfirmationOfDurationRequestBody,
} from './CSSZSickLeaveCertificateConfirmationOfDurationForm';
import useCSSZSickLeaveForm from '../Hooks/useCSSZSickLeaveForm';
import { formatCSSZSystemAddress } from '../../Helpers/CSSZ.helper';
import Loading from '../../../../Components/Loading';
import { combineExamDateAndTime } from './CSSZSickLeaveCertificateConfirmationOfDuration.helper';

export function buildConfirmationPayload(
	values: CSSZSickLeaveCertificateConfirmationOfDurationFormValues,
): CSSZSickLeaveCertificateConfirmationOfDurationRequestBody {
	const { shouldScheduleEvent, eventData, ...rest } = values;
	return {
		...rest,
		shouldScheduleEvent,
		eventData:
			shouldScheduleEvent &&
			eventData?.date != null &&
			eventData?.timeFrom &&
			eventData?.timeTo
				? {
						dateFrom: combineExamDateAndTime(
							eventData.date,
							eventData.timeFrom,
						),
						dateTo: combineExamDateAndTime(eventData.date, eventData.timeTo),
				  }
				: null,
	};
}

interface Props {
	item: CSSZSickLeaveCertificate;
	patientId: string;
	onCreate: () => void;
}

const CSSZSickLeaveCertificateConfirmationOfDuration: React.FC<Props> = ({
	item,
	patientId,
	onCreate,
}) => {
	const { post, isLoading } = useCSSZSickLeaveForm({
		patientId,
		sickLeaveCertificate: item,
		endpoint: 'potvrzeni-pracovni-neschopnosti',
	});

	const handleSubmit = async (
		values: CSSZSickLeaveCertificateConfirmationOfDurationFormValues,
	) => {
		await post(
			buildConfirmationPayload(values) as Parameters<typeof post>[0],
			onCreate,
		);
	};

	if (isLoading) {
		return <Loading />;
	}

	return (
		<div className={styles.wrapper}>
			<div className={styles.header}>
				<div className={styles.inline}>
					<Text
						tx='component.CSSZSickLeaveCertificateConfirmationOfDuration.evidenceNumber'
						className={styles.title}
						colon
					/>
					<Text tx={item.data.decisionNumber} className={styles.bold} />
				</div>
			</div>
			<div className={styles.items}>
				<div className={styles.item}>
					<Text
						tx='component.CSSZSickLeaveCertificateConfirmationOfDuration.surnameAndName'
						className={styles.title}
						colon
					/>
					<Text tx={item.data.userName} className={styles.bold} />
				</div>
				<div className={styles.item}>
					<Text
						tx='component.CSSZSickLeaveCertificateConfirmationOfDuration.birthNumber'
						className={styles.title}
						colon
					/>
					<Text tx={item.data.birthNumber} className={styles.bold} />
				</div>
				<div className={styles.item}>
					<Text
						tx='component.CSSZSickLeaveCertificateConfirmationOfDuration.address'
						className={styles.title}
						colon
					/>
					<Text
						tx={formatCSSZSystemAddress(item.data.patientAddress)}
						className={styles.bold}
					/>
				</div>
				<div className={styles.item}>
					<Text
						tx='component.CSSZSickLeaveCertificateConfirmationOfDuration.employer'
						className={styles.title}
						colon
					/>
					<Text tx={item.data.employment.employer} className={styles.bold} />
				</div>
			</div>
			<CSSZSickLeaveCertificateConfirmationOfDurationForm
				onSubmit={handleSubmit}
			/>
		</div>
	);
};

export default CSSZSickLeaveCertificateConfirmationOfDuration;
